<template>
  <div v-html="htmlContent" v-show="!isLoading"></div>
  <div class="featured-villages" id="featured-villages" v-if="isLoading">
    <div class="image-gallery" :class="{'mt-8': !showTitle}">
      <h2 v-if="showTitle">Featured Retirement Villages</h2>

      <div class="d-flex flex-column flex-md-row" style="column-gap: 30px">
        <div class="tile__premium-custom-image shine"></div>
        <div class="tile__premium-custom-image shine"></div>
        <div class="tile__premium-custom-image shine"></div>
      </div>
    </div>
  </div>
</template>
<script setup>
import {onBeforeMount, ref, nextTick, onMounted} from 'vue';
import axios from 'axios';
import {tns} from 'tiny-slider/src/tiny-slider';

const props = defineProps({
  showTitle: {
    type: Boolean,
    default: true,
  },
})

const htmlContent = ref('');
const isLoading = ref(true);
const tinySlider = ref(null);

const controls = [
    '<i class=\'icon icon--arrow-left\'></i><span class="sr-only">Previous slide</span>',
    '<i class=\'icon icon--arrow-right\'></i><span class="sr-only">Next slide</span>',
  ]

const featuredVillageClick = async (e) => {
  const ele = e.target;
  window.dataLayer = window.dataLayer || [];
  dataLayer.push({
    'event': 'productClick',
    'ecommerce': {
      'click': {
        'actionField': {'list': 'featured carousel'},
        'products': [
          {
            'id': ele.dataset.id,
            'name': ele.dataset.village,
            'brand': ele.dataset.operator,
            'category': 'village',
            'position': ele.dataset.position,
            'subscription': ele.dataset.subscription,
            'region': ele.dataset.region,
            'district': ele.dataset.district,
          }],
      },
    },
  });

  await nextTick();

  let loc = '';

  if (ele.href === undefined) {
    loc = ele.dataset.href;
  } else {
    loc = ele.href;
  }

  document.location = loc;
}

const tinyIndexChange = (e) => {
  const slideCount = parseInt(e.slideCount, 10);
  let premiumVillagesCount = parseInt(localStorage.getItem('premiumVillagesCount'), 10);

  if (slideCount/3 > premiumVillagesCount) {
    const ele = tinySlider.value.container
      ? tinySlider.value.$el
      : document.getElementById('featured-villages');

    if (isElementInViewPort(ele)) {
      let premiumVillagesIndex = localStorage.getItem('premiumVillagesIndex');

      if (premiumVillagesIndex === null || !premiumVillagesIndex.includes(e.navCurrentIndex)) {
        promotionViewDataLayer(e.navCurrentIndex, slideCount);
      }
    }
  }
}

const isElementInViewPort = (el) => {
  let top = el.offsetTop;
  let left = el.offsetLeft;
  const width = el.offsetWidth;
  const height = el.offsetHeight;

  while(el.offsetParent) {
    el = el.offsetParent;
    top += el.offsetTop;
    left += el.offsetLeft;
  }

  return (
    top < (window.pageYOffset + window.innerHeight) &&
    left < (window.pageXOffset + window.innerWidth) &&
    (top + height) > window.pageYOffset &&
    (left + width) > window.pageXOffset
  );
}

const handleScroll = () => {
  const premiumVillagesCount = parseInt(localStorage.getItem('premiumVillagesCount'), 10);
  const slideCount = parseInt(tinySlider.value.getInfo().slideCount, 10);

  if (slideCount/3 > premiumVillagesCount) {
    const ele = tinySlider.value.container
      ? tinySlider.value.$el
      : document.getElementById('featured-villages');
    if (isElementInViewPort(ele)) {
      promotionViewDataLayer(0, slideCount);
    }
  }
}

const promotionViewDataLayer = (navCurrentIndex, slideCount) => {
  const featuredVillages = document.getElementById('featured-villages');
  let premiumVillagesCount = parseInt(localStorage.getItem('premiumVillagesCount'), 10);
  const premiumVillagesIndex = localStorage.getItem('premiumVillagesIndex');
  const villages = JSON.parse(featuredVillages.dataset.villages);

  let start = 0;
  let end = (slideCount >= 3) ? 2 : (slideCount-1);

  if (navCurrentIndex === 2) {
    start = 6;
    end = (slideCount === 1) ? 8 : (slideCount-1);
  } else if (navCurrentIndex === 1) {
    start = 3;
    end = (slideCount >= 6) ? 5 : (slideCount-1);
  }

  window.dataLayer = window.dataLayer || [];
  for(let i = start; i <= end; i++) {
    const villageInfo = villages[i];
    window.dataLayer.push({
      'event': 'productImpression',
      'ecommerce': {
        'impressions': [{
          'name': villageInfo.name,
          'id': villageInfo.id,
          'price': '0',
          'brand': villageInfo.creative,
          'category': 'village',
          'list': 'featured carousel',
          'subscription': villageInfo.subscription,
          'region': villageInfo.region,
          'district': villageInfo.district,
          'position': villageInfo.position
        }],
      },
    });
  }
  premiumVillagesCount = premiumVillagesCount + 1;
  localStorage.setItem('premiumVillagesCount', premiumVillagesCount.toString());
  localStorage.setItem('premiumVillagesIndex', `${premiumVillagesIndex}, ${navCurrentIndex}`);
}

onBeforeMount(async () => {
  const response = await axios.get('/api/featured-villages');

  if (response.data.noItems === 'true') {
    isLoading.value = false;
    return;
  }

  htmlContent.value = response.data.html

  // Ensure DOM is updated before initializing the slider
  await nextTick();

  if (!props.showTitle) {
    const titleElement = document.querySelector('.featured-villages .image-gallery h2');

    if (titleElement) {
      titleElement.style.display = 'none';
      document.querySelector('.featured-villages .image-gallery').style.marginTop = '25px';
    }
  }

  // tiny slider render
  tinySlider.value = tns({
    container: '#tinySliderFeatured',
    items: 1,
    slideBy: 'page',
    autoplay: false,
    mouseDrag: true,
    loop: false,
    speed: 500,
    controlsText: controls,
    gutter: 30,
    responsive: {
      768: {
        items: 2,
        nav: true
      },
      992: {
        items: 3
      }
    },
  });

  // Attach event listener to dynamically added links
  document.querySelectorAll('.featured-link').forEach((link) => {
    link.addEventListener('click', featuredVillageClick);
  });

  const slideCount = parseInt(tinySlider.value.getInfo().slideCount, 10);
  localStorage.setItem('premiumVillagesCount', '0');
  localStorage.removeItem('premiumVillagesIndex');
  window.addEventListener('scroll', handleScroll);

  if (slideCount > 3) {
    tinySlider.value.events.on('indexChanged', tinyIndexChange);
  }

  isLoading.value = false;
})

</script>
